/**
 * ActionOverview — compact, collapsible summary of a proposal's reasoning.
 *
 * Expanded (default): Ahoy mark + "Action Overview" title + the reasoning
 * summary. Collapsed: the reasoning animates away, leaving just the title +
 * chevron.
 *
 * Standard triggers surface as "View X" shortcut buttons in the DETAIL
 * HEADER, beside the proposal title (see `useTriggerShortcutButtons`) — they
 * no longer render inside this card. High-priority triggers (see
 * `isHighPriorityTrigger` — currently email threads) render as their own
 * always-visible cards BELOW the overview.
 */

import { useCallback, useId, useMemo, useState } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { AccordionChevron } from '@/lib/base/accordion';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { AhoyLogoMinimal } from '@/lib/foundations/logo/ahoy-logo-minimal';
import type { ProposalDetail } from '@/lib/proposals/core/types';
import { isHighPriorityTrigger } from '@/lib/proposals/core/types';
import {
  getTriggerActionIds,
  useTriggerPreview,
} from '@/lib/proposals/hooks/useTriggerPreview';
import { cx } from '@/utils/cx';

import { TimelineConnector } from './shared';
import { DetailPanelCard } from './shared/DetailPanelCard';
import TriggerPreview from './TriggerPreview';

interface ActionOverviewProps {
  /** The proposal — supplies reasoning summary, org, and the primary action id. */
  proposal: ProposalDetail;
  /** Whether trigger data is expected (mirrors TriggerPreview's gate). */
  hasTrigger: boolean;
  /**
   * Start with the reasoning visible (the default). Collapsing hides the
   * reasoning, leaving just the title + shortcut buttons.
   */
  defaultExpanded?: boolean;
  /**
   * Trigger action ids to fetch/cache under, overriding derivation from
   * `proposal`. When `proposal` is a "visible" view with no-op actions filtered
   * out, its `actions[0]` may no longer be the trigger's source action — so the
   * caller passes ids derived from the ORIGINAL (unfiltered) proposal to keep
   * the trigger fetch correct. See {@link getTriggerActionIds}.
   */
  triggerActionIds?: { fetchActionId: number; cacheActionId: number };
  /**
   * Callback to open the same `kind:'email_thread'` slideout the draft's
   * "View thread" opens. Forwarded to the high-priority `<TriggerPreview>`
   * so an email-thread trigger card's affordance opens the thread slideout.
   */
  onOpenThread?: () => void;
}

export default function ActionOverview({
  proposal,
  hasTrigger,
  defaultExpanded = true,
  triggerActionIds,
  onOpenThread,
}: ActionOverviewProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const panelId = useId();
  const toggle = useCallback(() => setIsExpanded(prev => !prev), []);
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  // Prefer the AI-generated reasoning summary; fall back to the proposal
  // description when no summary was generated (e.g. onboarding proposals carry
  // their explanatory copy in `description`, not `reasoning_summary`).
  const reasoning = proposal.reasoning_summary || proposal.description;
  // Fetch the current version, cache under the root — see getTriggerActionIds.
  // Must match TriggerPreview exactly or the two stop deduping onto one request.
  // Prefer the caller-supplied ids (derived from the original, unfiltered
  // proposal) so hiding no-op actions can't shift which action the trigger
  // fetch keys on.
  const { fetchActionId, cacheActionId } =
    triggerActionIds ?? getTriggerActionIds(proposal);
  // Stable reference: this object is passed as a prop to <TriggerPreview>
  // below, so it must not be a fresh literal on every render (FRONTEND_PATTERNS
  // § Stable References).
  const stableTriggerActionIds = useMemo(
    () => ({ fetchActionId, cacheActionId }),
    [fetchActionId, cacheActionId]
  );

  // Fetch triggers to detect high-priority ones (which render as their own
  // cards below). The detail header's shortcut buttons and TriggerPreview
  // fetch the same query key — React Query dedupes, so there's no double
  // request.
  const {
    data: triggers,
    isLoading,
    isPlaceholderData,
  } = useTriggerPreview(proposal.org, proposal.id, fetchActionId, {
    enabled: fetchActionId > 0,
    cacheActionId,
  });

  // `useTriggerPreview` keeps the previously-viewed proposal's triggers as
  // placeholder data (keepPreviousData) while the switch fetches. That stale
  // data belongs to a different proposal, so ignore it here.
  const currentTriggers = isPlaceholderData ? undefined : triggers;

  // High-priority triggers render as their own always-visible cards below the
  // overview; standard triggers surface as shortcut buttons in the detail
  // header (see useTriggerShortcutButtons).
  const hasPriorityTriggers = useMemo(
    () =>
      Array.isArray(currentTriggers)
        ? currentTriggers.some(isHighPriorityTrigger)
        : false,
    [currentTriggers]
  );

  // The reasoning is the collapsible content, so the card is only expandable
  // when there's reasoning to hide/reveal.
  const showDisclosure = Boolean(reasoning);
  const open = showDisclosure && isExpanded;

  // True while the (async) trigger fetch is in flight, so `hasPriorityTriggers`
  // can't be trusted yet. `isPlaceholderData` covers the proposal-switch case,
  // where the new proposal's fetch is in flight but stale data is still
  // cached. Gate that branch on `actionId > 0`: a trigger-marked proposal with
  // no action leaves the query disabled (never fetches), yet keepPreviousData
  // holds the prior proposal's data with `isPlaceholderData` stuck true — so
  // without the gate it would never settle.
  const isResolvingTriggers =
    (isLoading || (isPlaceholderData && fetchActionId > 0)) && hasTrigger;

  // The reasoning is the only collapsible content now that the shortcut
  // buttons live in the detail header.
  const hasCollapsibleContent = Boolean(reasoning);

  // The disclosure itself is still gated on reasoning, so a proposal with
  // shortcut buttons but no reasoning renders no chevron. With no control to
  // re-open it, that card's content must stay visible — otherwise its buttons
  // would collapse shut with no way to reach them.
  const contentVisible = showDisclosure ? isExpanded : true;

  // When no high-priority trigger card renders below it, the overview is the
  // last card in the chain and draws the connector down to the first action
  // itself. When one *does* render, that card draws its own trailing connector
  // and a second line here would double up. Shortcut buttons are irrelevant to
  // this — they live inside the card, so they never fill the gap below it.
  //
  // Held back until the trigger fetch settles: `hasPriorityTriggers` is
  // transiently false while loading, so an ungated connector would flash in and
  // then vanish once a priority trigger arrives. Also requires an action to
  // connect *to*, so the line can't dangle off an actionless proposal.
  const showConnector =
    !isResolvingTriggers && !hasPriorityTriggers && proposal.actions.length > 0;

  return (
    <div
      // A connector's `h-8` box fills the gap to the next card exactly (it
      // carries no negative margin), so any bottom margin here would reopen
      // the gap it just closed. Margin therefore applies only when nothing
      // below this card draws a connector.
      className={hasPriorityTriggers || showConnector ? undefined : 'mb-4'}
      data-testid='action-overview'
    >
      <DetailPanelCard>
        {/* Self-managed disclosure (local `isExpanded` state) rather than the
            base <Accordion>: the collapsible content (the reasoning) sits in
            the middle of the header grid with the buttons below it, a shape
            the base accordion's trigger/panel split doesn't model. */}
        {/* `group/item` + `data-expanded` reproduce the contract <AccordionItem>
            normally provides, so the shared <AccordionChevron> rotates from CSS
            exactly as it does on the trigger and action cards. This card can't
            use <AccordionItem> itself — its collapsible content sits in the
            middle of the header grid (see the note below) — but it can still
            honour the same contract rather than hand-rolling a chevron. */}
        <div
          className='group/item relative'
          data-expanded={open ? '' : undefined}
        >
          {/* Overlay toggle: an absolutely-positioned button covering the
              card toggles the reasoning open/closed. The shortcut buttons sit
              above it in a `pointer-events-auto` layer and stop propagation.
              Only rendered when there's reasoning to hide/reveal. */}
          {showDisclosure && (
            <button
              type='button'
              aria-expanded={isExpanded}
              aria-controls={panelId}
              aria-label={
                isExpanded
                  ? 'Collapse action overview'
                  : 'Expand action overview'
              }
              onClick={toggle}
              className='absolute inset-0 w-full cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600'
            />
          )}

          <div className='pointer-events-none relative'>
            {/* Two-column grid: col 1 is the Ahoy mark; col 2 stacks the
                header, the (collapsible) reasoning, and the shortcut buttons.
                Reasoning/buttons pin to `col-start-2` so they align under the
                title, not under the icon. */}
            <div className='grid grid-cols-[auto_1fr] gap-x-2.5 px-4 pt-4 pb-4'>
              {/* col 1 · row 1 — Ahoy mark */}
              <AhoyLogoMinimal className='size-5 flex-shrink-0 self-center text-fg-brand-primary' />

              {/* col 2 · row 1 — title + expand/collapse chevron */}
              <div className='flex items-center gap-2'>
                <span className='flex-1 text-sm font-semibold text-utility-brand-700'>
                  Action overview
                </span>
                {showDisclosure && <AccordionChevron />}
              </div>

              {/* col 2 · row 2 — the collapsible content: the reasoning
                  summary AND the shortcut buttons. Collapsing hides both, so
                  the card shrinks to just the title + chevron. Grid-rows
                  transition (1fr → 0fr) animates them away. The content layer
                  is `pointer-events-none` so bare clicks fall through to the
                  overlay toggle; rendered markdown links, entity badges, and
                  `ahoy:` links (`[&_a]`, `[&_button]`, `[&_[role=button]]`)
                  and the button row re-enable pointer events so they stay
                  interactive without letting ordinary text clicks bypass the
                  toggle. */}
              {hasCollapsibleContent && (
                <div
                  id={panelId}
                  inert={!contentVisible ? true : undefined}
                  className={cx(
                    'col-start-2 grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
                    contentVisible ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                  )}
                >
                  <div className='min-h-0 overflow-hidden'>
                    {reasoning && (
                      <div className='pt-1 [&_a]:pointer-events-auto [&_button]:pointer-events-auto [&_[role=button]]:pointer-events-auto'>
                        <MarkdownContent
                          content={reasoning}
                          size='compact'
                          className='text-sm text-[var(--color-text-secondary)]'
                          orgId={orgId}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DetailPanelCard>

      {/* High-priority triggers are surfaced prominently as their own cards
          below the overview — always visible, never bundled in the accordion.
          That card draws the connector down to the actions itself. */}
      {hasPriorityTriggers && (
        <div className='mt-2'>
          <TriggerPreview
            proposal={proposal}
            hasTrigger={hasTrigger}
            include='high-priority'
            triggerActionIds={stableTriggerActionIds}
            onOpenThread={onOpenThread}
          />
        </div>
      )}

      {/* With no trigger card below it, the overview is the last card in the
          chain, so it links itself to the first action. */}
      {showConnector && <TimelineConnector />}
    </div>
  );
}
